"use client";

import { useTranslations } from "next-intl";
import type { CSSProperties, Ref, RefObject } from "react";

import IconMuteFill from "@ui/central-icons/icons/IconMuteFill";
import IconVolumeFullFill from "@ui/central-icons/icons/IconVolumeFullFill";
import { PreviewPlayerButton } from "@ui/components/core/PreviewPlayer/PreviewPlayerButton";
import { PreviewPlayerProgressRing } from "@ui/components/core/PreviewPlayer/PreviewPlayerProgressRing";
import { useVideoPlaybackState } from "@ui/components/core/PreviewPlayer/useVideoPlaybackState";

// The deck's one sound control — a pure mute/unmute toggle for the deck-wide sound state, with
// the progress ring tracking the front clip's playback. It deliberately lives OUTSIDE the 3D
// scene, as a plain screen-space element: rendered inside the projected card subtree it went
// through a shrink-then-magnify raster chain that never resolved sharp, whatever the texture
// resolution. The deck positions its center over the front card's projected corner-arc center —
// initially via `style`, then per animation frame through `buttonRef` — so it still reads as
// sitting on the card while rendering like any other UI control.
export function DeckSoundButton({
  buttonRef,
  onToggleSound,
  soundOn,
  style,
  videoRef,
}: {
  // Lets the deck's per-frame writer keep the button's position in sync during glides without
  // a React render.
  buttonRef?: Ref<HTMLButtonElement>;
  onToggleSound: () => void;
  soundOn: boolean;
  // The projected screen position (left/top/opacity) of the front card's corner, for the first
  // paint; the per-frame writer takes over from there.
  style?: CSSProperties;
  videoRef: RefObject<HTMLVideoElement | null>;
}) {
  const tGlobal = useTranslations("ClientKeys.Globals");
  const { currentTime, duration, isPlaying, progressAnimationStartedAt } = useVideoPlaybackState(videoRef);

  // A pure mute/unmute toggle — the clip itself always keeps playing (the deck's playback sync
  // owns that).
  function handleClick() {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    onToggleSound();

    // Unmuting a clip that isn't running (a just-ended clip, an out-of-view pause) also starts
    // it, so sound never turns on into a frozen frame.
    if (!soundOn && video.paused) {
      void video.play();
    }
  }

  // The icon shows the click's effect: a filled speaker while muted (click for sound), a slashed
  // one while sound is on (click to mute). The progress ring tracks the actual clip playback
  // regardless of mute.
  const label = soundOn ? tGlobal("mute") : tGlobal("unmute");

  return (
    <button
      ref={buttonRef}
      aria-label={label}
      aria-pressed={soundOn}
      className='tw-group/preview-button tw-pointer-events-auto tw-absolute tw-z-10 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-rounded-full tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-outline-focus'
      onClick={handleClick}
      style={style}
      title={label}
      type='button'
    >
      <PreviewPlayerButton
        // Larger than the sm play/pause glyph box (tw-size-2.5): the speaker reads too small
        // there, so it gets its own. isActive is pinned on purely for its look: the control
        // sits at the pressed-in scale permanently instead of popping between the two sizes.
        icon={soundOn ? <IconMuteFill className='tw-size-4' /> : <IconVolumeFullFill className='tw-size-4' />}
        isActive
        size='sm'
      >
        <PreviewPlayerProgressRing
          currentTime={currentTime}
          duration={duration}
          isActive={isPlaying}
          progressAnimationStartedAt={progressAnimationStartedAt}
          size='sm'
        />
      </PreviewPlayerButton>
    </button>
  );
}
